import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FlaskConical,
  Plus,
  ChevronRight,
  ChevronLeft,
  Clock,
  DollarSign,
  Users,
  Eye,
  Pencil,
  Search,
  Filter,
} from 'lucide-react';
import { api } from '@/lib/api';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } },
};

interface Study {
  id: string;
  title: string;
  status: string;
  compensation_amount: number;
  compensation_type: string;
  duration_minutes: number;
  max_participants: number;
  current_participants: number;
  category: string | null;
  created_at: string;
  starts_at: string;
  ends_at: string;
}

type StatusFilter = '' | 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';

export default function MyStudies() {
  const [studies, setStudies] = useState<Study[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await api.getMyStudies({ page, per_page: 10 });
        if (!mounted) return;
        const allStudies: Study[] = data.data ?? [];
        const filtered = statusFilter ? allStudies.filter((s) => s.status === statusFilter) : allStudies;
        setStudies(filtered);
        setTotalPages(data.total_pages ?? 1);
      } catch {
        // silently fail
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [page, statusFilter]);

  const statusCounts: Record<string, number> = {};
  studies.forEach((s) => { statusCounts[s.status] = (statusCounts[s.status] || 0) + 1; });

  return (
    <motion.div variants={fadeUp} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="page-header">My Studies</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage and track all your research studies
          </p>
        </div>
        <Link to="/create-study" className="btn-primary">
          <Plus className="w-4 h-4" />
          Create Study
        </Link>
      </div>

      {/* Status Filter */}
      <div className="flex gap-2 flex-wrap">
        {(['', 'draft', 'active', 'paused', 'completed', 'cancelled'] as StatusFilter[]).map((status) => (
          <button
            key={status || 'all'}
            onClick={() => { setStatusFilter(status); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              statusFilter === status
                ? 'bg-brand-500/10 border-brand-500/40 text-brand-600 dark:text-brand-400'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300'
            }`}
          >
            {status || 'All'}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="h-5 bg-gray-200 dark:bg-gray-800 rounded w-1/3 mb-3" />
              <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/2 mb-2" />
              <div className="flex gap-4">
                <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-16" />
                <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : studies.length === 0 ? (
        <div className="text-center py-16">
          <FlaskConical className="w-14 h-14 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-1">
            {statusFilter ? `No ${statusFilter} studies` : 'No studies yet'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
            {statusFilter ? 'Try a different filter' : 'Create your first study to get started'}
          </p>
          {!statusFilter && (
            <Link to="/create-study" className="btn-primary inline-flex">
              <Plus className="w-4 h-4" />
              Create Your First Study
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {studies.map((study, i) => {
            const fillPct = (study.current_participants / Math.max(study.max_participants, 1)) * 100;
            return (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link
                  to={`/my-studies/${study.id}`}
                  className="card p-5 flex items-center gap-4 group hover:border-brand-200 dark:hover:border-brand-800 transition-all"
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500/10 to-accent-500/10 flex items-center justify-center">
                    <FlaskConical className="w-5 h-5 text-brand-500" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                        {study.title}
                      </h3>
                      <StatusBadge status={study.status} />
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" /> ${study.compensation_amount}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {study.duration_minutes} min
                      </span>
                      {study.category && <span>{study.category}</span>}
                    </div>
                  </div>

                  {/* Participants bar */}
                  <div className="hidden sm:block flex-shrink-0 w-32">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                      <span><Users className="w-3 h-3 inline" /> Participants</span>
                      <span>{study.current_participants}/{study.max_participants}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-500 rounded-full transition-all"
                        style={{ width: `${fillPct}%` }}
                      />
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-300 dark:text-gray-600 flex-shrink-0 group-hover:text-brand-500 transition-colors" />
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-ghost disabled:opacity-40">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-ghost disabled:opacity-40">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </motion.div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: 'badge bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
    active: 'badge-success',
    paused: 'badge-warning',
    completed: 'badge-brand',
    cancelled: 'badge-danger',
  };
  return <span className={styles[status] || 'badge'}>{status}</span>;
}
